import * as fns from 'date-fns';
import {
    sort,
    order,
    insightsGroupBy,
} from '../utils/getDefaultFilterLocalStorage';

//"yyyy-MM-dd'T'HH:mm:ss.SSSXXX"

const startDate = fns.format(
    fns.startOfDay(fns.subMonths(new Date(), 3)),
    "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
);
const endDate = fns.format(
    fns.endOfDay(new Date()),
    "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
);

const clientInitialState = {
    filters: {
        userDefaultFilters: {
            id: null,
            filterJson: {},
        },
        isLoading: false,
        startDate,
        endDate,
        // `null` means the legacy fixed startDate/endDate pair is the source of
        // truth; otherwise it holds the bounds' type and value.
        dateSpec: null,
        compareDates: {
            option: undefined,
            dates: undefined,
        },
        changesId: null,
        overridesId: null,
        hasSeveralRightsholders: false,
        shouldShowLeagueHierarchy: false,
        isReturningFromOverride: false,
        // Use `null` (not `undefined`) so the key survives JSON.stringify when
        // clients are persisted to localStorage. `R.evolve` in getFilters.js
        // only writes transformations for keys that already exist in the
        // source state (it iterates `for (key in object)`), and JSON.stringify
        // drops undefined-valued properties — so without this, getFilters
        // could never set rightsholderAttributionMode after a localStorage
        // round-trip.
        rightsholderAttributionMode: null,
        rightsholders: {
            list: {},
            selected: [],
        },
        sponsors: {
            list: {},
            selected: [],
        },
        placements: {
            list: {},
            selected: [],
        },
        placementTypes: {
            list: {},
            selected: [],
        },
        accOwnTypes: {
            list: {},
            selected: [],
        },
        mediaTypes: {
            list: {},
            selected: [],
        },
        platformPostTypePairs: {
            list: {},
            selected: [],
        },
        audiencePlatforms: {
            list: {},
            selected: [],
        },
        rightsholdersLeaguePairs: {},
        accountUsernames: [],
        networks: [],
        hashtags: [],
        captions: [],
        collections: {
            list: {},
            selected: [],
        },
        mvpScore: 0,
        broadcastNames: [],
        broadcastOptions: [],
        insightsGroupBy,
        sort,
        order,
        validRsps: [],
        countries: {
            list: {},
            selected: [],
        },
        broadcastNetworks: {
            list: {},
            selected: [],
        },
        authorshipTypes: [],
        collaboratingAccountUsernames: [],
        ageBrackets: [],
        genders: [],
    },
    home: {
        cards: {
            isLoading: true,
            hasError: false,
            changesId: null,
            topPlatform: {},
            topPlacement: {},
            totalImpressions: null,
            totalSpMediaValue: null,
            totalFullMediaValue: null,
            totalVideoViews: null,
        },
        mediaValueByAccType: {
            'media-type': {
                changesId: null,
                isLoading: true,
                hasError: false,
                values: [],
            },
            social: {
                changesId: null,
                isLoading: false,
                hasError: false,
                values: [],
            },
            broadcast: {
                changesId: null,
                isLoading: false,
                hasError: false,
                values: [],
            },
            owned: {
                changesId: null,
                isLoading: false,
                hasError: false,
                values: [],
            },
        },
        topPlacement: {
            total: {
                changesId: null,
                isLoading: false,
                hasError: false,
                list: [],
            },
            graph: {
                changesId: null,
                isLoading: false,
                hasError: false,
                labels: [],
                data: {},
                granularity: 'week',
            },
        },
        topPlatform: {
            total: {
                changesId: null,
                isLoading: false,
                hasError: false,
                list: [],
            },
            graph: {
                changesId: null,
                isLoading: false,
                hasError: false,
                labels: [],
                data: {},
                granularity: 'week',
            },
        },
        topPlacements: {
            changesId: null,
            isLoading: false,
            labels: [],
            data: {},
            list: [],
        },
        topPlatforms: {
            changesId: null,
            isLoading: false,
            labels: [],
            data: {},
            list: [],
        },
        socialStream: {
            // After filters change, this value bellow sets to loading
            isLoading: true,
            posts: {},
            postsOrder: [],
        },
    },
    insights: {
        changesId: null,
        isLoading: false,
        lastQuery: {
            groupBy: [],
            orderBy: [],
        },
        total: {},
        values: [],
        dateRange: null,
        compareTotal: {},
        compareValues: [],
        compareDateOption: null,
        compareDateRange: null,
        hasCompareDates: false,
        allValues: [],
    },
    media: {
        isLoading: true,
        changesId: null,
        overridesId: null,
        hasMore: true,
        currentPage: 0,
        posts: {},
        postsOrder: [],
        totals: {},
        filtersOverride: null,
        isLoadingAllPosts: true,
        allPosts: {},
        allPostsOrder: [],
        allPostTotals: {},
        overridingFiltersElement: [],
    },
    socialAccounts: {
        facebook: {
            isRequestingLinkedPages: true,
            accessToken: null,
            linkedPages: {},
            pages: {},
        },
        instagram: {
            isRequestingLinkedAccounts: true,
            accessToken: null,
            linkedAccounts: {},
            accounts: {},
        },
    },
    collections: {
        create: {
            isLoading: false,
        },
        list: {
            isLoading: false,
            isArchiving: false,
            items: {},
        },
    },
    notifications: [],
    overrides: {
        logout: false,
        primaryColor: null,
        secondaryColor: null,
    },
    history: [],
    scheduledReports: [],
    settings: {
        client: {
            rsps: [],
        },
    },
    broadcasts: {
        isLoading: true,
        changesId: null,
        overridesId: null,
        byId: {},
        order: [],
        hasMore: true,
        names: [],
        currentPage: 0,
        filtersOverride: null,
        overridingFiltersElement: [],
    },
    accounts: {
        isLoading: true,
        changesId: null,
        byId: {},
        order: [],
    },
    pacing: {
        isLoading: true,
        isLoadingLimit: true,
        limit: null,
        goals: [],
        availableGoalCount: null,
    },
    socialBenchmarking: {
        isLoading: true,
        isLoadingLeagues: true,
        isLoadingFavorites: true,
        accountGroups: [],
        favoritesList: null,
        leagues: [],
        average: {
            postCount: 0,
            postCountPerDay: 0,
        },
        highlights: {},
        periodStartDate: '',
        periodEndDate: '',
        hasIncompleteData: false,
        hasNoData: false,
    },
    reports: {
        byId: {},
        show: false,
    },
};

export default clientInitialState;
